import { useCallback, useEffect, useRef, useState } from "react";
import { formatErr } from "@/lib/api";

const EMPTY = [];
export const useCalendarPage = ({ client, path, enabled = true, yearEnabled = true, initialTicket = "" }) => {
  const [choice, setChoice] = useState({ path, year: "latest" });
  const [targetTicket, setTargetTicket] = useState(initialTicket);
  const requested = choice.path === path ? choice.year : "latest";
  const key = `${path}|${requested}|${targetTicket}|${yearEnabled}`;
  const [state, setState] = useState({ key: "", data: null, loading: true, error: "", denied: false });
  const controller = useRef(null), serial = useRef(0);
  const metadata = useRef({ path: "", years: [], latest: null });
  const reload = useCallback(async () => {
    if (!enabled || !path) return;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    const n = ++serial.current;
    setState(previous => ({ key, data: previous.key === key ? previous.data : null, loading: true, error: "", denied: previous.key === key && previous.denied }));
    try {
      const { data } = await client.get(path, { params: yearEnabled ? { calendar_year: requested, ...(targetTicket ? { focus_ticket: targetTicket } : {}) } : {}, signal: abort.signal });
      if (!abort.signal.aborted && n === serial.current) {
        metadata.current = { path, years: data.available_years || [], latest: data.available_years?.[0] || data.year };
        setState({ key, data, loading: false, error: "", denied: false });
      }
    } catch (error) {
      if ([401, 403].includes(error.response?.status) && !abort.signal.aborted && n === serial.current) metadata.current = { path: "", years: [], latest: null };
      if (!abort.signal.aborted && n === serial.current) setState(previous => ({ key, data: null, loading: false,
        denied: [401, 403].includes(error.response?.status) || (previous.key === key && previous.denied),
        error: error.response?.data?.detail ? formatErr(error.response.data.detail) : "Could not load this year. Please try again." }));
    }
  }, [client, path, enabled, yearEnabled, requested, targetTicket, key]);
  const cancel = useCallback(() => { ++serial.current; controller.current?.abort(); }, []);
  useEffect(() => { reload(); return cancel; }, [reload, cancel]);
  const current = state.key === key ? state : { data: null, loading: true, error: "" };
  const setItems = useCallback(update => setState(previous => {
    if (previous.key !== key || !previous.data) return previous;
    const rows = Array.isArray(previous.data) ? previous.data : previous.data.items || [];
    const items = typeof update === "function" ? update(rows) : update;
    return { ...previous, data: Array.isArray(previous.data) ? items : { ...previous.data, items } };
  }), [key]);
  const selectYear = useCallback(year => { setTargetTicket(""); setChoice({ path, year: String(year) }); }, [path]);
  const selectTicket = useCallback(ticket => { setTargetTicket(ticket); }, []);
  return { data: current.data, items: (Array.isArray(current.data) ? current.data : current.data?.items) || EMPTY,
    loading: enabled && current.loading, error: current.error, reload, setItems, selectYear, selectTicket,
    year: current.denied ? null : current.data?.year || (requested !== "latest" ? Number(requested) : metadata.current.path === path ? metadata.current.latest : null),
    years: current.data?.available_years || (metadata.current.path === path ? metadata.current.years : []), requested };
};